import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileArchive, FileText, Image as ImageIcon, Mail, Subtitles } from "lucide-react";
import { API, api } from "@/lib/api";
import { TourPlayer } from "@/components/TourPlayer";
import { useI18n } from "@/context/I18nContext";

const PILL = "inline-flex min-h-[38px] items-center gap-1.5 rounded-full border border-white/15 px-4 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand";

export default function Press() {
  const { lang } = useI18n();
  const [kit, setKit] = useState(null);

  useEffect(() => {
    api.get("/press", { params: { lang } })
      .then(({ data }) => setKit(data))
      .catch((e) => console.error("could not load the press kit", e));
  }, [lang]);

  const say = kit?.words || {};

  useEffect(() => {
    document.title = say.h_tour ? `${say.kicker} · ChatMiLu` : "Press kit · ChatMiLu";
  }, [say.kicker, say.h_tour]);

  if (!kit) return <div className="mx-auto max-w-5xl px-5 py-14" data-testid="press-loading" />;

  return (
    <div className="mx-auto max-w-5xl px-5 py-14 sm:px-8" data-testid="press-page">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-brand">{say.kicker}</p>
      <h1 className="mt-2 font-display text-4xl font-bold leading-tight sm:text-5xl">{say.title}</h1>
      <p className="mt-3 max-w-2xl text-base text-ink-soft">{say.intro}</p>

      <a
        data-testid="press-kit-zip"
        href={`${API}/press/kit.zip?lang=${lang}`}
        className="mt-6 inline-flex min-h-[46px] items-center gap-2 rounded-full bg-brand px-6 text-sm font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95"
      >
        <FileArchive className="h-4 w-4" /> {say.zip}
      </a>

      <section className="mt-12" data-testid="press-tour">
        <h2 className="text-base font-semibold md:text-lg">{say.h_tour}</h2>
        <div className="mt-3">
          <TourPlayer />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <a data-testid="press-script-text" href={`${API}/tour/script.txt?lang=${lang}`} className={PILL}>
            <FileText className="h-3.5 w-3.5" /> {say.script}
          </a>
          <a data-testid="press-script-srt" href={`${API}/tour/script.srt?lang=${lang}`} className={PILL}>
            <Subtitles className="h-3.5 w-3.5" /> {say.srt}
          </a>
          <a data-testid="press-social-card" href="/tour/share.jpg" download className={PILL}>
            <ImageIcon className="h-3.5 w-3.5" /> {say.card}
          </a>
        </div>
      </section>

      <section className="mt-12" data-testid="press-boilerplate">
        <h2 className="text-base font-semibold md:text-lg">{say.h_copy}</h2>
        <p className="mt-3 rounded-2xl border border-white/10 bg-panel p-4 text-sm leading-relaxed"
           data-testid="press-short" dir="auto">
          <span className="block text-[11px] font-semibold uppercase tracking-widest text-ink-soft">
            {say.short_label}
          </span>
          {say.short}
        </p>
        <p className="mt-3 rounded-2xl border border-white/10 bg-panel p-4 text-sm leading-relaxed"
           data-testid="press-long" dir="auto">
          <span className="block text-[11px] font-semibold uppercase tracking-widest text-ink-soft">
            {say.long_label}
          </span>
          {say.long}
        </p>
      </section>

      <section className="mt-12" data-testid="press-facts">
        <h2 className="text-base font-semibold md:text-lg">{say.h_facts}</h2>
        <dl className="mt-3 grid gap-3 sm:grid-cols-2">
          {kit.facts.map((fact) => (
            <div key={fact.term} className="rounded-2xl border border-white/10 bg-panel p-4">
              <dt className="text-[11px] font-semibold uppercase tracking-widest text-brand" dir="auto">
                {fact.term}
              </dt>
              <dd className="mt-1 text-sm text-ink-soft" dir="auto">{fact.said}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mt-12" data-testid="press-shots">
        <h2 className="text-base font-semibold md:text-lg">{say.h_shots}</h2>
        <p className="mt-1 text-xs text-ink-soft">{say.shots_note}</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {kit.shots.map((shot) => (
            <a
              key={shot.file}
              href={`/tour/${shot.file}`}
              download
              data-testid={`press-shot-${shot.file.replace(".jpg", "")}`}
              className="group overflow-hidden rounded-2xl border border-white/10 bg-panel transition-colors hover:border-brand/50"
            >
              <img src={`/tour/${shot.file}`} alt={shot.label}
                   className="aspect-[16/10] w-full object-cover object-top" />
              <span className="flex items-center justify-between gap-2 px-3 py-2 text-[11px] text-ink-soft"
                    dir="auto">
                <span className="truncate">{shot.label}</span>
                <Download className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
              </span>
            </a>
          ))}
        </div>
      </section>

      <section className="mt-12" data-testid="press-marks">
        <h2 className="text-base font-semibold md:text-lg">{say.h_marks}</h2>
        <div className="mt-3 flex flex-wrap gap-3">
          {kit.marks.map((mark) => (
            <a
              key={mark.file}
              href={`/${mark.file}`}
              download
              data-testid={`press-mark-${mark.file.replace(".png", "")}`}
              className="flex min-w-[150px] flex-col items-center gap-2 rounded-2xl border border-white/10 bg-panel p-4 transition-colors hover:border-brand/50"
            >
              <img src={`/${mark.file}`} alt={mark.label} className="h-12 w-auto" />
              <span className="text-[11px] text-ink-soft" dir="auto">{mark.label}</span>
            </a>
          ))}
        </div>
        <p className="mt-3 text-xs text-ink-soft" dir="auto">{say.guidance}</p>
      </section>

      <section className="mt-12 rounded-3xl border border-white/10 bg-panel p-6" data-testid="press-contact">
        <h2 className="text-base font-semibold md:text-lg">{say.h_talk}</h2>
        <p className="mt-1 text-sm text-ink-soft" dir="auto">{say.talk}</p>
        <a
          data-testid="press-email"
          href="mailto:support@chatmilu.com?subject=Press%20enquiry"
          className="mt-4 inline-flex min-h-[42px] items-center gap-2 rounded-full bg-brand px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover"
        >
          <Mail className="h-4 w-4" /> support@chatmilu.com
        </a>
        <p className="mt-4 text-xs text-ink-soft">
          <Link to="/tour" className="text-brand" data-testid="press-tour-link">chatmilu.com/tour</Link>
        </p>
      </section>
    </div>
  );
}
